// Voice messages, screen recordings and the file viewer of a ticket (60.11.553, backend 1.0.0.703): the pure rules.
import {TicketAttachment} from './support-ticket-types'

export type TicketAttachmentKind = 'voice' | 'video' | 'image' | 'pdf' | 'text' | 'csv' | 'file'

const baseType = (contentType: string) => (contentType || '').split(';')[0].trim().toLowerCase()
const extensionOf = (fileName: string) => (fileName.split('.').pop() || '').toLowerCase()

/** What a ticket file is, for how it is shown: by its type, and by its name for a plain text one. */
export const ticketAttachmentKind = (
  file: Pick<TicketAttachment, 'contentType' | 'originalFileName'>
): TicketAttachmentKind => {
  const type = baseType(file.contentType)
  const extension = extensionOf(file.originalFileName || '')
  if (type.startsWith('audio/')) return 'voice'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('image/')) return 'image'
  if (type === 'application/pdf') return 'pdf'
  if (type === 'text/csv' || extension === 'csv') return 'csv'
  if (type.startsWith('text/') || extension === 'txt') return 'text'
  return 'file'
}

/** Whether the viewer shows a file itself (the rest is only downloaded). */
export const canViewInPanel = (kind: TicketAttachmentKind) => kind !== 'file'

/** The first type of $candidates the browser records in, or '' when it records none of them. */
export const pickRecordingType = (
  candidates: string[],
  isTypeSupported: ((type: string) => boolean) | undefined
) => {
  if (!isTypeSupported) return ''
  return candidates.find((type) => isTypeSupported(type)) || ''
}

export const VOICE_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]
export const SCREEN_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
]

/** The type a recording is uploaded as - without its codecs - and its file's extension. */
export const recordingFileType = (recorded: string, kind: 'voice' | 'screen') => {
  const type = baseType(recorded) || (kind === 'voice' ? 'audio/webm' : 'video/webm')
  const sound = kind === 'voice'
  if (type.endsWith('/mp4'))
    return {type: sound ? 'audio/mp4' : 'video/mp4', extension: sound ? 'm4a' : 'mp4'}
  if (type.endsWith('/ogg')) return {type: 'audio/ogg', extension: 'ogg'}
  return {type: sound ? 'audio/webm' : 'video/webm', extension: 'webm'}
}

const pad = (value: number) => String(value).padStart(2, '0')

/** «voice-20261021-101500.webm»: the recording's name, by the moment it ended (the browser's clock). */
export const recordingFileName = (kind: 'voice' | 'screen', extension: string, now = new Date()) =>
  `${kind}-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(
    now.getHours()
  )}${pad(now.getMinutes())}${pad(now.getSeconds())}.${extension}`

/** Whether a file's name says it was recorded here, and how: a voice message or the screen. */
export const recordedKind = (fileName: string): 'voice' | 'screen' | null =>
  /^voice-\d{8}-\d{6}\.[a-z0-9]+$/i.test(fileName)
    ? 'voice'
    : /^screen-\d{8}-\d{6}\.[a-z0-9]+$/i.test(fileName)
    ? 'screen'
    : null

/** «۱:۰۵»: minutes and seconds, in Persian digits. */
export const clockLabel = (seconds: number) => {
  const whole = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0))
  return `${Math.floor(whole / 60).toLocaleString('fa-IR')}:${pad(whole % 60).replace(
    /\d/g,
    (digit) => Number(digit).toLocaleString('fa-IR')
  )}`
}

/** The rows of a CSV file for its table: quoted fields, commas and line ends inside quotes; at most $limit rows. */
export const parseCsv = (text: string, limit = 500) => {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  const source = text.replace(/^﻿/, '')
  const delimiter =
    !source.split('\n')[0].includes(',') && source.split('\n')[0].includes(';') ? ';' : ','
  for (let index = 0; index < source.length && rows.length < limit; index++) {
    const character = source[index]
    if (quoted) {
      if (character === '"' && source[index + 1] === '"') {
        field += '"'
        index++
      } else if (character === '"') quoted = false
      else field += character
    } else if (character === '"') quoted = true
    else if (character === delimiter) {
      row.push(field)
      field = ''
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && source[index + 1] === '\n') index++
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else field += character
  }
  if ((field || row.length) && rows.length < limit) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

/** The most a screen recording may take: under the ticket's 20 MB a file, with room for its last second. */
export const SCREEN_MAX_SECONDS = 5 * 60
export const SCREEN_MAX_BYTES = 19 * 1024 * 1024
export const SCREEN_VIDEO_BITS = 400_000
export const VOICE_MAX_SECONDS = 10 * 60
